import { useRef } from "react";
import { Autocomplete, CircularProgress, ClickAwayListener, MenuItem, TextField } from "@mui/material";
import colors from "assets/theme-dark/base/colors";
import { NoOptionPopper } from "./NoOptionPopper";
import { InfiniteAutocompleteProps, useInfiniteAutocomplete } from "../../hooks/useInfiniteAutocomplete";

export type AutoObject = {
  id: string;
  value: string;
};

export function InfiniteAutocomplete(infiniteProps: InfiniteAutocompleteProps): JSX.Element {
  const { customRenderOption, noOptionNode, isMoreToFetch, label, fetchMore, error, onChange, small, loading } =
    infiniteProps;

  const { options, handleInputChange, open, setOpen, autoRef, inputRef, state, inputChangeLoading, onSelectChange } =
    useInfiniteAutocomplete(infiniteProps);

  const renderOption = (props: React.HTMLAttributes<HTMLLIElement>, object: string): JSX.Element => {
    const selected = options.find((x) => x.value === object) ?? null;
    if (customRenderOption && selected) {
      return customRenderOption(props, selected);
    }
    return <MenuItem {...props}>{object}</MenuItem>;
  };

  const optionsRef = useRef<AutoObject[]>([]);
  optionsRef.current = options;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div style={{ width: "100%" }}>
        <Autocomplete
          ref={autoRef}
          freeSolo
          value={state.value}
          noOptionsText={noOptionNode ? noOptionNode : "No options"}
          onFocus={() => setOpen(true)}
          open={open}
          disablePortal
          options={options.map((x) => x.value)}
          sx={{
            width: "100%",
            position: "relative",
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: colors.text.main },
              "&:hover fieldset": { borderColor: colors.primary.main },
              "&.Mui-focused fieldset": { borderColor: colors.primary.main },
              color: colors.text.main,
            },
            "& .MuiInputBase-input": { color: colors.text.main },
            "& .MuiInputLabel-root": { color: colors.grey[900] },
            "& .MuiInputLabel-root.Mui-focused": { color: colors.grey[500] },
            "& .MuiAutocomplete-listbox": {
              maxHeight: "250px",
              overflowY: "auto",
              padding: "8px 0",
            },
            "& .MuiAutocomplete-popper": {
              zIndex: 50,
            },
          }}
          onInputChange={(_, value, reason) => {
            handleInputChange(value, reason);
          }}
          onChange={(_, value, reason) => onSelectChange(value, reason)}
          renderOption={(props, object) => renderOption(props, object)}
          ListboxProps={{
            style: {
              maxHeight: "350px",
              zIndex: 50,
            },
            onScroll: (event: React.SyntheticEvent) => {
              const listboxNode = event.currentTarget;

              if (listboxNode.scrollTop + listboxNode.clientHeight === listboxNode.scrollHeight && isMoreToFetch) {
                fetchMore();
              }
            },
          }}
          renderInput={(params) => (
            <TextField
              inputRef={inputRef}
              {...params}
              label={label}
              name={label}
              error={Boolean(error)}
              required={typeof onChange !== "undefined"}
              size={small ? "small" : undefined}
              InputProps={{
                ...params.InputProps,
                endAdornment: error ? (
                  <div>error={error}</div>
                ) : (
                  <>
                    {loading || inputChangeLoading ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <NoOptionPopper
          anchorEl={autoRef.current}
          open={open && options.length === 0 && !loading}
          noOptionNode={noOptionNode}
        />
      </div>
    </ClickAwayListener>
  );
}
